import { errorText } from '../../../lib/apiErrors';
import { wikiErrorCode, wikiErrorStatus } from '../../../hooks/useWikiV2';
import { WIKI_IMPORT_ERRORS } from './wikiModalCopy';

/**
 * The message a create modal shows for a failed request — the server's own
 * copy (`detail.message`, BE-core contract §0) whenever it sends one, never
 * axios's "Request failed with status code 415". Bare string codes from the
 * guards (`"wiki_category_access_denied"`) get readable copy here; there is no
 * prototype copy for these (the prototype never talked to a server).
 */
const CODE_COPY = Object.freeze({
    wiki_category_access_denied: 'You can\'t add to that category. Ask a Wiki admin for edit access.',
    wiki_category_not_found: 'That category no longer exists. Choose another one.',
    wiki_category_archived: 'That category is archived. Choose another one.',
    // #872: archive, restore and move from the app.
    wiki_category_not_archived: 'That category isn\'t archived any more. Refresh to see it.',
    wiki_page_archived: 'That page is already archived.',
    wiki_page_not_archived: 'That page isn\'t archived any more. Refresh to see it.',
    wiki_page_access_denied: 'You can\'t change that page. Ask a Wiki admin for edit access.',
    wiki_page_write_denied: 'You can\'t change that page. Ask a Wiki admin for edit access.',
    wiki_invalid_location: 'That location isn\'t valid any more. Choose another one.',
    wiki_org_required: 'Your account isn\'t part of an organization, so it can\'t add to the Wiki.',
    upload_type_not_allowed: WIKI_IMPORT_ERRORS.type,
    upload_too_large: WIKI_IMPORT_ERRORS.size,
});

const CODE_RE = /^[a-z0-9_.:-]+$/;
const FALLBACK = 'Something went wrong. Please try again.';

export function wikiModalErrorText(error, fallback = FALLBACK) {
    if (!error) return fallback;
    if (!error.response) {
        return error.isAxiosError || error.request
            ? 'Couldn\'t reach the Wiki. Check your connection and try again.'
            : fallback;
    }
    const detail = error.response.data?.detail;
    if (detail && typeof detail === 'object' && !Array.isArray(detail)
        && typeof detail.message === 'string' && detail.message.trim()) {
        return detail.message;
    }
    const code = wikiErrorCode(error);
    if (code && CODE_COPY[code]) return CODE_COPY[code];
    if (typeof detail === 'string' && /^Permission denied/i.test(detail)) {
        return 'Your role can\'t make this change in the Wiki.';
    }
    const status = wikiErrorStatus(error);
    if (status === 413) return WIKI_IMPORT_ERRORS.size;
    if (status === 415) return WIKI_IMPORT_ERRORS.type;
    // A bare machine code is never user copy.
    if (typeof detail === 'string' && CODE_RE.test(detail.trim())) return fallback;
    return errorText(error, fallback);
}

const PLAN_ERRORS = new Set(['plan_limit_reached', 'feature_not_available']);
const PLAN_CODES = new Set(['wiki_limit_reached', 'wiki_feature_disabled']);
const PLAN_FALLBACK = 'Your plan doesn\'t include this. Ask an admin to upgrade.';

/**
 * A plan refusal (PLAN-CP contract §2: 403 `wiki_limit_reached` for
 * categories / pages / storage, 403 `wiki_feature_disabled`) →
 * `{ code, key, message }`, else null. `message` is the server's user-facing
 * copy ("The Starter plan allows 10 top-level categories. …").
 *
 * Only 403s: those are what the app-wide `PlanErrorInterceptor` answers with
 * its upgrade modal (a z-50 Radix dialog). The create modals close on a plan
 * refusal and show the message in the Wiki toast, so the upgrade modal is not
 * buried under the Wiki modal (z 91+). Other refusals stay inline.
 */
export function wikiPlanRefusal(error) {
    if (wikiErrorStatus(error) !== 403) return null;
    const detail = error.response.data && error.response.data.detail;
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return null;
    if (!PLAN_ERRORS.has(detail.error) && !PLAN_CODES.has(detail.code)) return null;
    return {
        code: detail.code || detail.error,
        key: detail.key || detail.feature || detail.resource || null,
        message: typeof detail.message === 'string' && detail.message.trim() ? detail.message : PLAN_FALLBACK,
    };
}

export default wikiModalErrorText;
